import { useCallback, useEffect, useState } from 'react'
import { fetchInbox, fetchPlayer, fetchRoster, patchNote } from '../data/api'
import type { InboxItem, PlayerFile, RosterEntry } from '../types'
import { SectionLabel } from './RoundTimeline'
import { fmtDate, fmtStamp } from '../data/fmt'
import { useCanCoach, useSession } from '../data/session'
import { Compare } from './Compare'

// Individual development, which nothing in the portal could answer before. Every number here
// already existed — the season fold builds a full per-match series per player, the scouting fold
// splits it per map, and every match's AI review writes a line about each player. The problem was
// that all of it was only ever readable one match at a time, so "is this the third week running
// we've told him the same thing?" had no answer.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const
const pct = (n: number) => `${Math.round(n)}%`

export function Players({ onOpen }: { onOpen?: (demoId: string, round?: number) => void }) {
  const [roster, setRoster] = useState<RosterEntry[] | null>(null)
  const [sid, setSid] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // A player's file is every review they have ever been given (PLAN §3), so a player gets one
  // button: their own. Coaches get the squad. The API refuses the rest either way.
  const canCoach = useCanCoach()
  const { user } = useSession()

  useEffect(() => {
    fetchRoster()
      .then((r) => {
        const mine = canCoach ? r : r.filter((p) => p.steamid64 === user?.steamid64)
        setRoster(mine)
        setSid((cur) => cur ?? mine[0]?.steamid64 ?? null)
      })
      .catch((e) => setErr((e as Error).message))
  }, [canCoach, user?.steamid64])

  // The inbox renders whatever the stats say. A player added mid-season has homework waiting before
  // they've appeared on a single scoreboard, and an early return would have hidden exactly that.
  const stats =
    err !== null ? (
      <p className="text-sm text-loss">Couldn't load the roster: {err}</p>
    ) : !roster ? (
      <p className="text-sm text-text-dim">Loading…</p>
    ) : roster.length === 0 ? (
      <p className="text-sm text-text-faint">
        {canCoach
          ? "No matches of ours parsed yet, so there's nobody to show."
          : 'Nothing here yet: no parsed match of ours has your Steam account on the scoreboard.'}
      </p>
    ) : null

  if (stats) {
    return (
      <div className="space-y-5">
        <Inbox onOpen={onOpen} />
        {stats}
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <Inbox onOpen={onOpen} />
      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="P0" label="Squad" />
        <div className="mt-3 flex flex-wrap gap-1.5">
          {roster!.map((p) => (
            <button
              key={p.steamid64}
              onClick={() => setSid(p.steamid64)}
              title={
                [...(p.nick ? [p.name] : []), ...(p.aliases ?? [])].length
                  ? `In game: ${[...(p.nick ? [p.name] : []), ...(p.aliases ?? [])].join(', ')}`
                  : undefined
              }
              className="px-3 py-1.5 text-xs"
              style={{
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                background: sid === p.steamid64 ? 'var(--accent)' : 'transparent',
                color: sid === p.steamid64 ? 'var(--accent-ink)' : 'var(--text-dim)',
              }}
            >
              {p.nick ?? p.name}
              <span className="ml-2 tabular-nums opacity-70">{p.rating.toFixed(2)}</span>
            </button>
          ))}
        </div>
      </section>

      {sid && <PlayerDetail sid={sid} />}
      {canCoach && <Compare initial={sid ?? undefined} />}
    </div>
  )
}

function PlayerDetail({ sid }: { sid: string }) {
  const [p, setP] = useState<PlayerFile | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setP(null)
    setErr(null)
    fetchPlayer(sid)
      .then(setP)
      .catch((e) => setErr((e as Error).message))
  }, [sid])

  if (err) return <p className="text-sm text-loss">Couldn't load that player: {err}</p>
  if (!p) return <p className="text-sm text-text-dim">Loading…</p>

  const t = p.totals
  return (
    <>
      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="P1" label={`${p.nick ?? p.name} — this season`} />
        {/* Everything below spans matches, some played under another name — say which, or the
            numbers look like they belong to someone else. */}
        {(p.nick || p.aliases.length > 0) && (
          <p className="mt-2 text-[11px] text-text-faint">
            In game: {[...(p.nick ? [p.name] : []), ...p.aliases].join(', ')} — same account, one
            history.
          </p>
        )}
        {t ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat label="Rating" value={t.rating.toFixed(2)} sub={`${t.demos} demo${t.demos === 1 ? '' : 's'}`} />
            <Stat label="ADR" value={String(Math.round(t.adr))} sub={`${t.kills}–${t.deaths} K/D`} />
            <Stat label="KAST" value={pct(t.kast)} sub="rounds you did something" />
            <Stat
              label="Untraded deaths"
              value={String(t.untradedDeaths)}
              sub={`${t.tradeKills} trade kills · ${t.tradedDeaths} traded`}
            />
          </div>
        ) : (
          /* A new player's first visit is a blank page that reads like a broken one. It isn't:
             there is nothing here because nobody has uploaded a demo they were in yet, and saying
             so — plus what will appear, and who to ask — is the difference between "not built" and
             "not yet". This is the page a player is most likely to open first. */
          <div className="mt-3 space-y-2">
            <p className="text-sm text-text-dim">
              Nothing here yet — no parsed demo has {p.nick ?? p.name} in it so far.
            </p>
            <p className="text-xs leading-relaxed text-text-faint">
              As soon as one lands, this page fills in on its own: rating, ADR and KAST for the
              season, form match by match, and every "work on this" line a coach or the AI review has
              written about you — gathered here rather than left scattered across matches you'd have
              to remember to open. Nothing to set up. If you've played and it's still empty, the demo
              probably hasn't been uploaded — ask your coach.
            </p>
          </div>
        )}
      </section>

      {/* Form. The season page draws one rating sparkline; the underlying series has five metrics
          in it, and "his ADR is climbing while the rating flatlines" is the more useful read. */}
      {p.trends.length > 1 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="P2" label="Form, match by match" />
          <div className="mt-4 overflow-x-auto">
            {/* No forced min-width: on a phone this became 1.4 screens of sideways dragging to
                read one row. The two columns a player opens this page for are the opponent and the
                rating; KAST and K–D are what they scroll past. Those hide below sm and the table
                fits, rather than fitting nothing and scrolling. */}
            <table className="w-full border-collapse text-sm sm:min-w-140">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-text-faint">
                  <th className="px-2 py-1.5 text-left font-medium">Match</th>
                  <th className="hidden px-2 py-1.5 text-left font-medium sm:table-cell">Map</th>
                  <th className="px-2 py-1.5 text-right font-medium">Rating</th>
                  <th className="px-2 py-1.5 text-right font-medium">ADR</th>
                  <th className="hidden px-2 py-1.5 text-right font-medium sm:table-cell">KAST</th>
                  <th className="hidden px-2 py-1.5 text-right font-medium sm:table-cell">K–D</th>
                </tr>
              </thead>
              <tbody>
                {[...p.trends].reverse().map((x) => (
                  <tr key={x.demoId} className="border-t border-border/60 hover:bg-surface-2">
                    <td className="px-2 py-2 text-text">
                      {x.opponent || '—'}
                      <span className="ml-2 text-[11px] text-text-faint">
                        {x.finishedAt ? fmtDate(x.finishedAt) : ''}
                      </span>
                    </td>
                    <td className="hidden px-2 py-2 text-text-dim sm:table-cell">{x.map.replace('de_', '')}</td>
                    <td className="px-2 py-2 text-right tabular-nums" style={{ color: x.rating >= 1 ? 'var(--accent)' : 'var(--loss)' }}>
                      {x.rating.toFixed(2)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-text-dim">{Math.round(x.adr)}</td>
                    <td className="hidden px-2 py-2 text-right tabular-nums text-text-dim sm:table-cell">
                      {pct(x.kast)}
                    </td>
                    <td className="hidden px-2 py-2 text-right tabular-nums text-text-dim sm:table-cell">
                      {x.kills}–{x.deaths}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {p.maps.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="P3" label="By map" />
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {p.maps.map((m) => (
              <div key={m.map} className="border border-border bg-bg-2 p-3" style={{ borderRadius: 'var(--radius)' }}>
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-sm uppercase tracking-wider text-accent">
                    {m.map.replace('de_', '')}
                  </span>
                  <span className="tabular-nums text-sm text-text">{m.rating.toFixed(2)}</span>
                </div>
                <div className="mt-1 text-[11px] text-text-dim">
                  {Math.round(m.adr)} ADR · {pct(m.kast)} KAST · {m.kills}–{m.deaths}
                </div>
                {m.demos < 3 && (
                  <div className="mt-1 text-[11px] text-loss">
                    {m.demos} demo{m.demos === 1 ? '' : 's'} — thin
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* The point of the page. One "work on your crossfires" is a note; the same line three
          matches running is the thing that actually needs a practice session. */}
      {p.feedback.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="P4" label="What we've told you to fix" />
          {/* The repeats first. Reading twelve reviews and noticing the same sentence three times
              was the job this page left to the reader; it's a fold, so it shouldn't have been. */}
          {p.repeated.length > 0 && (
            <div className="mt-3 space-y-2">
              {p.repeated.map((r) => (
                <div
                  key={r.line}
                  className="border p-3"
                  style={{ borderRadius: 'var(--radius)', borderColor: 'var(--loss)', background: 'var(--bg-2)' }}
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-xs uppercase tracking-wider text-loss">
                      {r.times}× — {r.times >= 3 ? 'practise this' : 'said before'}
                    </span>
                    {r.lastAt && <span className="text-[11px] text-text-faint">last: {fmtDate(r.lastAt)}</span>}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-text">{r.line}</p>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-text-faint">
            {p.repeated.length > 0
              ? 'Below: every review in full, newest first.'
              : 'Newest first, from each match’s AI review. Nothing has been said twice yet — when it is, it moves to the top of this section.'}
          </p>
          <div className="mt-4 space-y-3">
            {[...p.feedback].reverse().map((f) => (
              <div key={f.demoId} className="border-l-2 border-border pl-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-display text-xs uppercase tracking-wider text-accent">
                    {f.map.replace('de_', '')} vs {f.opponent || '—'}
                  </span>
                  <span className="text-[11px] text-text-faint">
                    {f.finishedAt ? fmtDate(f.finishedAt) : ''}
                  </span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-text-dim">{f.assessment}</p>
                <p className="mt-1 text-xs leading-relaxed text-text">
                  <span className="text-loss">Fix: </span>
                  {f.improve}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  )
}

/** What's been aimed at you — assignments first, then mentions. Read from the per-user index, not
 *  by scanning every match's notes: this is the first thing a player's page loads. */
function Inbox({ onOpen }: { onOpen?: (demoId: string, round?: number) => void }) {
  const [items, setItems] = useState<InboxItem[] | null>(null)
  const load = useCallback(() => {
    fetchInbox()
      .then(setItems)
      .catch(() => setItems([]))
  }, [])
  useEffect(() => load(), [load])

  if (!items || items.length === 0) return null
  const open = items.filter((i) => !i.done).length

  return (
    <section className="border p-5" style={{ ...card, borderColor: open ? 'var(--accent)' : 'var(--border)' }}>
      <SectionLabel n="P!" label={open ? `For you — ${open} open` : 'For you'} />
      <div className="mt-4 space-y-3">
        {items.map((i) => (
          <div key={i.noteId} className="flex items-start gap-3" style={{ opacity: i.done ? 0.55 : 1 }}>
            <span
              className="mt-0.5 shrink-0 rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
              style={{
                background: i.kind === 'assigned' ? 'var(--accent)' : 'var(--border)',
                color: i.kind === 'assigned' ? 'var(--accent-ink)' : 'var(--text-dim)',
              }}
            >
              {i.kind === 'assigned' ? 'to do' : 'mention'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-relaxed text-text" style={{ textDecoration: i.done ? 'line-through' : 'none' }}>
                {i.body}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-text-faint">
                <span>{i.authorName}</span>
                <span>{fmtStamp(i.createdAt)}</span>
                <button onClick={() => onOpen?.(i.demoId, i.round)} className="text-accent hover:underline">
                  {i.round ? `watch round ${i.round}` : 'open the match'}
                </button>
                {i.kind === 'assigned' ? (
                  <button
                    onClick={async () => {
                      await patchNote(i.demoId, i.noteId, { done: !i.done }).catch(() => {})
                      load()
                    }}
                    className="hover:text-accent"
                  >
                    {i.done ? 'not done' : 'mark done'}
                  </button>
                ) : (
                  /* A mention is a nudge, and a nudge you've read should go away. The note stays;
                     only your copy of it clears. */
                  <button
                    onClick={async () => {
                      await patchNote(i.demoId, i.noteId, { dismiss: true }).catch(() => {})
                      load()
                    }}
                    className="hover:text-loss"
                    title="Clear this from your list — the note itself stays on the match"
                  >
                    clear
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border border-border bg-bg-2 p-3" style={{ borderRadius: 'var(--radius)' }}>
      <div className="font-display text-xs uppercase tracking-wider text-text-faint">{label}</div>
      <div className="mt-1 tabular-nums text-xl text-text">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-text-dim">{sub}</div>}
    </div>
  )
}
